import _ from 'underscore';
import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'react-jss';
import { BrowserRouter, Switch, Route } from 'react-router-dom';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n, validationLocalization } from '@bingads-webui/build-legacy-i18n';
import { isTestEnvironment } from '@bingads-webui-campaign/global-app-init';
import { GlobalErrorBoundary } from '@bingads-webui-react/global-error-boundary';
import { ComponentSettings, ThemeFluent } from '@bingads-webui-theme-2018/theme-fluent';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { ServerContextWithActionProvider } from '@bingads-webui-campaign-react/server-context';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import {
  getInstrumentationService,
  InstrumentationWithServiceProvider,
} from '@bingads-webui-campaign-react/pmax-lite-instrumentation';
import { getGlobalErrorOptions } from '@bingads-webui-campaign-react/global-error-options';
import { WrappedFloatingBannerContainer } from '@bingads-webui-react/notification-center';

import { IdpSelectionPage } from '@bingads-webui-campaign-react/idp-selection-page';
import { IdpErrorPage } from '@bingads-webui-campaign-react/idp-error-page';
import { RoutedLoginErrorPage } from '@bingads-webui-campaign-react/login-error-pages';

import { PMaxLiteLandingPageCore } from './feature/landing-page-core';
import { PMaxLiteSDKPageCore } from './feature/sdk-page-core';
import { signUpIdpSelectionUrl, signInIdpSelectionUrl, userInvitationIdpSelectionUrl, emailExistsErrorUrl, loginErrorUrl } from './path';

import mainI18n from './main.l10n';

const isTestEnv = isTestEnvironment(document.domain);

function PMaxLiteLandingApp(props) {
  const { serverContext, instrumentation, appPerfMarker } = props;
  const [i18nObjects, setI18n] = useState({ i18n: {}, legacyI18n: {} });

  const instrumentationService = useMemo(() => getInstrumentationService({
    instrumentation,
    serverContext,
  }), [instrumentation, serverContext]);
  const globalErrorOptions = getGlobalErrorOptions({ instrumentation });

  const {
    Permissions: {
      isGoogleAuthEnabled,
      isFacebookAuthEnabled,
      isAcceptInvitationClientSideFlowEnabled,
    },
  } = serverContext;

  const logger = useMemo(() => (
    instrumentation.pmaxLiteLanding.create().logger.create()
  ), [instrumentation]);

  useEffect(() => {
    mainI18n.ready().then((i18n) => {
      // eslint-disable-next-line no-param-reassign
      i18n.failFast = !isTestEnv ? 'SAFE' : i18n.failFast;
      const legacyI18n = buildLegacyI18n(i18n, [
        ValidationStringMap,
        validationLocalization,
      ]);
      setI18n({
        i18n,
        legacyI18n,
      });
    });
  }, []);

  if (_.isEmpty(i18nObjects.i18n)) {
    return null;
  }

  // The login error route completes the marker itself, once its card has mounted, so the timing
  // covers shell + card render rather than stopping at i18n-ready.
  if (window.location.pathname !== loginErrorUrl) {
    appPerfMarker.done();
  }

  return (
    <BrowserRouter>
      <ThemeProvider theme={{ components: ComponentSettings, core: ThemeFluent }}>
        <FluentProvider theme={webLightTheme}>
          <ServerContextWithActionProvider context={serverContext}>
            <I18nContext.Provider value={i18nObjects.i18n}>
              <LegacyI18nContext.Provider value={i18nObjects.legacyI18n}>
                <InstrumentationWithServiceProvider context={instrumentationService}>
                  <GlobalErrorBoundary {...globalErrorOptions}>
                    <Switch>
                      {(isGoogleAuthEnabled || isFacebookAuthEnabled) && <Route
                        path={signUpIdpSelectionUrl}
                        render={() => <IdpSelectionPage scenario="signup" logger={logger} />}
                      />}
                      <Route
                        path={signInIdpSelectionUrl}
                        render={() => <IdpSelectionPage scenario="signin" logger={logger} />}
                      />
                      <Route
                        path={userInvitationIdpSelectionUrl}
                        render={() => (<IdpSelectionPage
                          scenario="userInvitation"
                          logger={logger}
                          isAcceptInvitationClientSideFlow={isAcceptInvitationClientSideFlowEnabled}
                        />)}
                      />
                      <Route
                        path={emailExistsErrorUrl}
                        render={() => <IdpErrorPage logger={logger} signUpIdpSelectionUrl={signUpIdpSelectionUrl} />}
                      />
                      <Route
                        path={loginErrorUrl}
                        render={() => <RoutedLoginErrorPage perfMarker={appPerfMarker} />}
                      />
                      <Route
                        path="/"
                        render={
                          () => (isSDK()
                          ? <PMaxLiteSDKPageCore logger={logger} />
                          : <PMaxLiteLandingPageCore logger={logger} />)
                        }
                      />
                    </Switch>
                    <WrappedFloatingBannerContainer />
                  </GlobalErrorBoundary>
                </InstrumentationWithServiceProvider>
              </LegacyI18nContext.Provider>
            </I18nContext.Provider>
          </ServerContextWithActionProvider>
        </FluentProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

PMaxLiteLandingApp.propTypes = {
  serverContext: PropTypes.shape({
    PageTrackingId: PropTypes.string.isRequired,
    Urls: PropTypes.shape({
      FederatedLogin: PropTypes.string.isRequired,
    }).isRequired,
    Permissions: PropTypes.shape({
      isGoogleAuthEnabled: PropTypes.bool.isRequired,
      isFacebookAuthEnabled: PropTypes.bool.isRequired,
      isAcceptInvitationClientSideFlowEnabled: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  instrumentation: PropTypes.shape({
    pmaxLiteLanding: PropTypes.shape({
      create: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  appPerfMarker: PropTypes.shape({
    done: PropTypes.func.isRequired,
  }).isRequired,
};

export default PMaxLiteLandingApp;
